import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { CalendarBlank, ArrowRight } from "@phosphor-icons/react";

export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/blog").then(r => setPosts(r.data)).catch(()=>{}).finally(() => setLoading(false));
  }, []);
  return (
    <>
      <SEO title="Blog | Ehliyet ve Sürücülük Rehberi | Estem"
        description="Ehliyet, sürücü kursu, sınavlar ve trafik hakkında güncel rehberler. Estem Sürücü Kursu Blog."
        canonical="https://www.estemsurucukursu.com/blog"/>
      <section className="px-4 md:px-8 py-12 md:py-20 max-w-7xl mx-auto">
        <div className="nb-badge mb-4">Blog</div>
        <h1 className="font-heading font-bold text-5xl md:text-7xl tracking-tight leading-[1.05]">Bilgi paylaştıkça<br/><span className="font-serif italic font-normal text-brand-primary">çoğalır.</span></h1>
        {loading ? (
          <div className="mt-12 text-center text-stone-500">Yükleniyor…</div>
        ) : posts.length === 0 ? (
          <div className="mt-12 nb-card p-12 text-center text-stone-500">Yakında yeni yazılar burada olacak.</div>
        ) : (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="nb-card overflow-hidden group hover:-translate-y-1 transition-transform" data-testid={`blog-card-${p.slug}`}>
                {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-56 object-cover"/>}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-stone-500 font-semibold"><CalendarBlank weight="bold" size={14}/> {new Date(p.created_at).toLocaleDateString("tr-TR")}</div>
                  <h2 className="font-heading font-bold text-xl mt-3 leading-tight line-clamp-2">{p.title}</h2>
                  <p className="mt-3 text-sm text-stone-600 line-clamp-3">{p.excerpt}</p>
                  {p.tags && p.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">{p.tags.slice(0,3).map(t => <span key={t} className="text-[10px] px-2 py-0.5 bg-brand-primarySubtle text-brand-primary rounded-full font-semibold">{t}</span>)}</div>
                  )}
                  <div className="mt-4 flex items-center gap-2 font-semibold text-sm text-brand-primary">Devamını Oku <ArrowRight weight="bold" size={14}/></div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  // Article + BreadcrumbList schema
  useEffect(() => {
    if (!post) return;
    const base = "https://www.estemsurucukursu.com";
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.meta_description || post.excerpt,
        "image": post.cover_image || `${base}/estem-logo.jpg`,
        "datePublished": post.created_at,
        "dateModified": post.updated_at || post.created_at,
        "author": { "@type": "Organization", "name": "Estem Sürücü Kursu", "url": base },
        "publisher": {
          "@type": "Organization",
          "name": "Estem Sürücü Kursu",
          "logo": { "@type": "ImageObject", "url": `${base}/estem-logo.jpg`, "width": 200, "height": 60 }
        },
        "mainEntityOfPage": { "@type": "WebPage", "@id": `${base}/blog/${slug}` },
        "keywords": post.meta_keywords || (post.tags || []).join(", "),
        "inLanguage": "tr-TR"
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": base },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${base}/blog` },
          { "@type": "ListItem", "position": 3, "name": post.title, "item": `${base}/blog/${slug}` }
        ]
      }
    ];
    schemas.forEach((s, i) => {
      const id = `ld-blog-${i}`;
      let el = document.getElementById(id);
      if (!el) { el = document.createElement("script"); el.id = id; el.type = "application/ld+json"; document.head.appendChild(el); }
      el.textContent = JSON.stringify(s);
    });
    return () => {
      schemas.forEach((_, i) => { const el = document.getElementById(`ld-blog-${i}`); if (el) el.remove(); });
    };
  }, [post, slug]);

  if (notFound) return <div className="p-12 text-center text-stone-500">Yazı bulunamadı</div>;
  if (!post) return <div className="p-12 text-center text-stone-500">Yükleniyor…</div>;
  return (
    <>
      <SEO title={`${post.title} | Estem Blog`} description={post.meta_description || post.excerpt} canonical={`https://www.estemsurucukursu.com/blog/${slug}`} image={post.cover_image} keywords={post.meta_keywords} type="article"/>
      <article className="px-4 md:px-8 py-12 max-w-3xl mx-auto">
        <Link to="/blog" className="nb-label hover:text-brand-primary">← Blog</Link>
        {post.category && <div className="mt-4"><span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-brand-primarySubtle text-brand-primary uppercase tracking-wider">{post.category}</span></div>}
        <h1 className="mt-4 font-heading font-bold text-4xl md:text-6xl tracking-tight leading-[1.05]">{post.title}</h1>
        <div className="mt-4 flex items-center gap-3 text-sm text-stone-500 flex-wrap">
          <span>{new Date(post.created_at).toLocaleDateString("tr-TR")}</span>
          {post.tags && post.tags.length > 0 && (
            <>
              <span className="opacity-40">·</span>
              <div className="flex flex-wrap gap-1">{post.tags.map(t => <span key={t} className="text-[10px] px-2 py-0.5 bg-stone-100 text-stone-700 rounded-full font-semibold">{t}</span>)}</div>
            </>
          )}
        </div>
        {post.cover_image && <img src={post.cover_image} alt={post.title} className="mt-8 w-full h-80 md:h-96 object-cover rounded-2xl"/>}
        <div className="mt-8 prose prose-lg max-w-none prose-headings:font-heading prose-headings:font-bold prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-2xl prose-p:text-stone-800 prose-p:leading-relaxed prose-a:text-brand-primary prose-a:font-semibold prose-strong:text-brand-ink prose-ul:list-disc prose-ul:pl-6 prose-li:my-1" dangerouslySetInnerHTML={{ __html: post.content }} />
      </article>
    </>
  );
}
